import { Property, ReactSimpleComponentProps, TenantSimpleListData } from '@/custom-types';
import { useCallback, useMemo, useReducer, useState } from 'react';
import { CreateContractContext } from './CreateContractContext';
import { CreateContractInput, CreateRentChargeInput, LeaseTermOption } from '@/graphql/generated';
import { CreateContractReducer } from '@/reducers/CreateContractReducer';

const emptyState: CreateContractInput = {
  startDate: new Date(),
  depositAmount: 0,
  depositReceivedDate: null,
  fiatAmountFees: 0,
  finishDate: null,
  lateFeeDays: 0,
  leaseTermOption: LeaseTermOption.MonthToMonth,
  percentageOfRentFees: 0,
  rentCharges: [],
  signed: false,
  tenantId: '',
  unitiesIds: [],
  propertyId: '',
  dayForExecuteCharges: '1',
  isAutoGenerateRentCharge: false,
  monthlyChargeAmount: '0',
}

export const CreateContractContextProvider = ({ children }: ReactSimpleComponentProps) => {
  const [state, dispatch] = useReducer(CreateContractReducer, emptyState)
  const [isDisabled, setIsDisabled] = useState(false);
  const [property, setProperty] = useState<Property | null>(null)
  const [tenant, setTenant] = useState<TenantSimpleListData | null>(null)

  const setPropertyAction = useCallback((property: Property) => {
    dispatch({
      type: 'CREATE_CONTRACT-setProperty',
      payload: {
        propertyId: property.id,
      },
    })
    setProperty(property)
  }, [])

  const setUnitiesAction = useCallback((unitiesIds: string[]) => {
    dispatch({
      type: 'CREATE_CONTRACT-setUnities',
      payload: {
        unitiesIds,
      },
    })
  }, [])

  const setLeaseTermAction = useCallback((leaseTermOption: LeaseTermOption, startDate: Date, finishDate: Date | null) => {
    dispatch({
      type: 'CREATE_CONTRACT-setLeaseTerm',
      payload: {
        leaseTermOption,
        startDate,
        finishDate,
      }
    })
  }, [])

  const setTenantAction = useCallback((tenant: TenantSimpleListData | null) => {
    setTenant(tenant)
    dispatch({
      type: 'CREATE_CONTRACT-setTenant',
      payload: { tenantId: tenant?.user.id ?? '' }
    })
  }, [])

  const setRentChargesAction = useCallback((rentCharges: CreateRentChargeInput[]) => {
    dispatch({
      type: 'CREATE_CONTRACT-setRentCharges',
      payload: { rentCharges }
    })
  }, [])

  const setSecurityDepositAction = useCallback((amount: number, receivedDate?: Date) => {
    dispatch({
      type: 'CREATE_CONTRACT-setSecurityDeposit',
      payload: {
        amount,
        receivedDate: receivedDate || undefined,
      }
    })
  }, [])

  const setSignedAction = useCallback((isSigned: boolean) => {
    dispatch({
      type: 'CREATE_CONTRACT-setSigned',
      payload: { isSigned }
    })
  }, [])

  const setLateFeesAction = useCallback((lateFeeDays?: number, percentageOfRentFees?: number, fiatAmountFees?: number) => {
    dispatch({
      type: 'CREATE_CONTRACT-setLateFees',
      payload: {
        lateFeeDays,
        percentageOfRentFees,
        fiatAmountFees,
      }
    })
  }, [])

  const setDayForExecuteChargesAction = useCallback((day: string) => {
    dispatch({
      type: 'CREATE_CONTRACT-setDayForExecuteCharge',
      payload: { day }
    })
  }, [])

  const setAutoGenerateRentChargeAction = useCallback((isAutoGenerateRentCharge: boolean) => {
    dispatch({
      type: 'CREATE_CONTRACT-setAutoGenerateRentCharge',
      payload: { isAutoGenerateRentCharge }
    })
  }, [])

  const setMonthlyChargeAmountAction = useCallback((monthlyChargeAmount: string) => {
    dispatch({
      type: 'CREATE_CONTRACT-setMonthlyChargeAmount',
      payload: { monthlyChargeAmount }
    })
  }, [])

  const setButtonDisabled = useCallback(() => setIsDisabled(true), [])
  const setButtonEnabled = useCallback(() => setIsDisabled(false), [])

  const actions = useMemo(() => ({
    setProperty: setPropertyAction,
    setUnities: setUnitiesAction,
    setLeaseTerm: setLeaseTermAction,
    setTenant: setTenantAction,
    setRentCharges: setRentChargesAction,
    setSecurityDeposit: setSecurityDepositAction,
    setSigned: setSignedAction,
    setLateFees: setLateFeesAction,
    setDayForExecuteCharges: setDayForExecuteChargesAction,
    setAutoGenerateRentCharge: setAutoGenerateRentChargeAction,
    setMonthlyChargeAmount: setMonthlyChargeAmountAction,
    setButtonDisabled,
    setButtonEnabled,
  }), [setPropertyAction, setUnitiesAction, setLeaseTermAction, setTenantAction, setRentChargesAction, setSecurityDepositAction, setSignedAction, setLateFeesAction, setDayForExecuteChargesAction, setAutoGenerateRentChargeAction, setMonthlyChargeAmountAction, setButtonDisabled, setButtonEnabled])


  return (
    <CreateContractContext.Provider value={{
      ...actions,
      state,
      buttonState: isDisabled,
      property,
      tenant,
    }}>
      {children}
    </CreateContractContext.Provider>
  )
}